import { type FocusEvent, type MouseEvent, useCallback, useEffect, useId, useRef, useState } from "react";
import { TooltipBubble } from "./TooltipBubble";
import { type TooltipPosition, type TooltipSide, tooltipPosition } from "./tooltipPosition";

export interface TooltipAnchorProps {
  onMouseEnter: (e: MouseEvent<HTMLElement>) => void;
  onMouseLeave: () => void;
  onFocus: (e: FocusEvent<HTMLElement>) => void;
  onBlur: () => void;
  "aria-describedby"?: string;
}

/** Hover time before the tooltip shows (keyboard focus shows it at once). */
export const HOVER_DELAY_MS = 450;

/** Hides the tooltip as soon as anything scrolls, since it would no longer follow its anchor. */
function useHideOnScroll(visible: boolean, hide: () => void): void {
  useEffect(() => {
    if (!visible) return;
    window.addEventListener("scroll", hide, true);
    return () => window.removeEventListener("scroll", hide, true);
  }, [visible, hide]);
}

/**
 * Lightweight tooltip: spread `anchorProps` on the trigger and render
 * `tooltip`. Shows after a short delay on hover, immediately on keyboard focus.
 */
export function useTooltip(
  label: string,
  options: { side?: TooltipSide; disabled?: boolean } = {},
): { anchorProps: TooltipAnchorProps; tooltip: React.ReactNode } {
  const { side = "top", disabled = false } = options;
  const id = useId();
  const [position, setPosition] = useState<TooltipPosition | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const show = useCallback((anchor: HTMLElement) => setPosition(tooltipPosition(anchor.getBoundingClientRect(), side)), [side]);

  const hide = useCallback(() => {
    clearTimeout(timer.current);
    setPosition(null);
  }, []);

  useEffect(() => () => clearTimeout(timer.current), []);
  useHideOnScroll(position !== null, hide);

  const anchorProps: TooltipAnchorProps = {
    onMouseEnter: (e) => {
      if (disabled) return;
      const anchor = e.currentTarget;
      clearTimeout(timer.current);
      timer.current = setTimeout(() => show(anchor), HOVER_DELAY_MS);
    },
    onMouseLeave: hide,
    onFocus: (e) => {
      if (disabled) return;
      if (e.currentTarget.matches(":focus-visible")) show(e.currentTarget);
    },
    onBlur: hide,
    "aria-describedby": position ? id : undefined,
  };

  const tooltip = position && !disabled ? <TooltipBubble id={id} label={label} position={position} side={side} /> : null;

  return { anchorProps, tooltip };
}
